<script setup lang="ts">
import { PersianDate } from '@alireza-ab/vue3-persian-datepicker'
import { reactive, ref, watch } from 'vue'
import ChevronDown from '/svg/chevron-down.svg'

const props = defineProps<{
  name?: string
  modelValue: string | null
  label?: string
  placeholder?: string
  error?: string
  disabled?: boolean
  appearance?: string
}>()

const emit = defineEmits<(e: 'update:modelValue', value: string | null) => void>()

const isFocused = ref(false)
const internalDate = ref<string | null>(props.modelValue)
const datePickerRef = ref<any>(null)

const styles = {
  'primary-color': '#2d5396',
  'secondary-color': '#2d5396',
  'in-range-background': '#2d5396',
  radius: '12px',
}

const shortcut = reactive({
  'هم اکنون': [new PersianDate().toString()],
  دیروز: [new PersianDate().addDay(-1).toString()],
  فردا: [new PersianDate().addDay(1).toString()],
  'اول هفته': [new PersianDate().startOf('week').toString()],
  'آخر هفته': [new PersianDate().endOf('week').toString()],
})

function openPicker() {
  if (!props.disabled && datePickerRef.value) {
    datePickerRef.value.showPicker?.()
  }
}

function onUpdate(val: string | null) {
  emit('update:modelValue', val)
  internalDate.value = val
}
function toFaDate(d: string) {
  return new Intl.DateTimeFormat('fa-IR').format(new Date(d))
}
watch(
  () => props.modelValue,
  (v) => (internalDate.value = v)
)
</script>

<template>
  <div class="relative flex flex-col gap-1 w-full">

    <!-- Label -->
    <label v-if="label && modelValue"
      class="w-fit bg-white px-1 absolute ms-4 -mt-2 text-[10px] font-bold transition-all z-30 duration-400 pointer-events-none"
      :class="appearance === 'admin' ? 'text-[#6C6C6C]' : 'text-[#1b325a]'">
      {{ label }}
    </label>

    <div class="absolute z-10 cursor-pointer w-full h-12 rounded-xl border px-[18px] text-[13px] font-medium flex items-center bg-white transition border-[#E0E0E0]
                 hover:border-[#1b325a] disabled:bg-gray-100 disabled:cursor-not-allowed" @click="openPicker">
      <span class="text-[#1A1A1A] truncate">
        <template v-if="Array.isArray(internalDate)">
          {{ internalDate[0] ? toFaDate(internalDate[0]) : '' }}
          -
          {{ internalDate[1] ? toFaDate(internalDate[1]) : '' }}
        </template>
        <template v-else-if="internalDate && !Array.isArray(internalDate)">
          <span class="font-fa">
            {{ toFaDate(internalDate) }}
          </span>
        </template>
        <template v-else>
          {{ props.placeholder || 'انتخاب تاریخ' }}
        </template>
      </span>
    </div>

    <svg
      class="absolute top-6 z-10 end-3 -translate-y-1/2 w-4 h-4 pointer-events-none transition-transform duration-200"
      width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5.24848 8.99696C5.66186 8.99696 5.99696 8.66186 5.99696 8.24848C5.99696 7.83511 5.66186 7.5 5.24848 7.5C4.83511 7.5 4.5 7.83511 4.5 8.24848C4.5 8.66186 4.83511 8.99696 5.24848 8.99696ZM5.99696 10.7485C5.99696 11.1619 5.66186 11.497 5.24848 11.497C4.83511 11.497 4.5 11.1619 4.5 10.7485C4.5 10.3351 4.83511 10 5.24848 10C5.66186 10 5.99696 10.3351 5.99696 10.7485ZM8.00043 8.99696C8.41381 8.99696 8.74892 8.66186 8.74892 8.24848C8.74892 7.83511 8.41381 7.5 8.00043 7.5C7.58706 7.5 7.25195 7.83511 7.25195 8.24848C7.25195 8.66186 7.58706 8.99696 8.00043 8.99696ZM8.74892 10.7485C8.74892 11.1619 8.41381 11.497 8.00043 11.497C7.58706 11.497 7.25195 11.1619 7.25195 10.7485C7.25195 10.3351 7.58706 10 8.00043 10C8.41381 10 8.74892 10.3351 8.74892 10.7485ZM10.7485 8.99696C11.1619 8.99696 11.497 8.66186 11.497 8.24848C11.497 7.83511 11.1619 7.5 10.7485 7.5C10.3351 7.5 10 7.83511 10 8.24848C10 8.66186 10.3351 8.99696 10.7485 8.99696ZM14 4.5C14 3.11929 12.8807 2 11.5 2H4.5C3.11929 2 2 3.11929 2 4.5V11.5C2 12.8807 3.11929 14 4.5 14H11.5C12.8807 14 14 12.8807 14 11.5V4.5ZM3 6H13V11.5C13 12.3284 12.3284 13 11.5 13H4.5C3.67157 13 3 12.3284 3 11.5V6ZM4.5 3H11.5C12.3284 3 13 3.67157 13 4.5V5H3V4.5C3 3.67157 3.67157 3 4.5 3Z"
        fill="#1A1A1A" />
    </svg>

    <!-- <img :src="ChevronDown" alt="chevron" /> -->

    <div v-if="error" class="absolute bottom-0 flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
      <img src="/svg/error-icon.svg" width="14" alt="error-icon" />
      {{ error }}
    </div>

    <!-- Alireza Date Picker -->
    <ClientOnly>
      <alireza-date-picker ref="datePickerRef" @update:modelValue="onUpdate" v-model="internalDate" :input="false"
        :styles="styles" locale="fa" format="YYYY-MM-DD" display-format="jYYYY/jMM/jDD" :auto-submit="true"
        :modal="true" :shortcut="shortcut" mode="single"
        class="absolute z-50 font-fa w-full pt-2 h-12! focus-within:outline-0 opacity-0" />
    </ClientOnly>
  </div>
</template>

<style>
.vpd-icon-btn {
  display: none !important;
}

.form-control {
  border: none !important;
}

.vpd-main {
  width: 100% !important;
}

.pdp-year,
.pdp-month {
  color: #2d5396 !important;
  font-size: 13px !important;
  font-weight: 500 !important;
}

.pdp-weekday {
  font-size: 11.2px !important;
}

.pdp-month {
  margin-inline-end: 4px;
}

.pdp-day {
  font-size: 11.2px !important;
  width: 29.11px !important;
  height: 29.11px !important;
}

.pdp-shortcut {
  color: #000000;
  font-size: 14px;
}

.pdp-icon.pdp-pointer {
  display: none !important;
}
</style>
